import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const register = async (username, email, password, employee_number, first_name, last_name) => {
  const response = await api.post('/auth/register', { username, email, password, employee_number, first_name, last_name });
  if (response.data.token) {
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
  }
  return response.data;
};

export const login = async (email, password) => {
  const response = await api.post('/auth/login', { email, password });
  if (response.data.token) {
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
  }
  return response.data;
};

export const getMe = async () => {
  const response = await api.get('/auth/me');
  return response.data;
};

// Refresh user data from backend and update localStorage
export const refreshUserData = async () => {
  const token = localStorage.getItem('token');
  if (!token) return null;
  try {
    const response = await api.get('/auth/me');
    localStorage.setItem('user', JSON.stringify(response.data));
    return response.data;
  } catch {
    return null;
  }
};

// Password Reset
export const forgotPassword = async (email) => {
  const response = await api.post('/auth/forgot-password', null, { params: { email } });
  return response.data;
};

export const resetPassword = async (token, newPassword) => {
  const response = await api.post('/auth/reset-password', null, { params: { token, new_password: newPassword } });
  return response.data;
};

export const changePassword = async (newPassword) => {
  const response = await api.post('/auth/change-password', { new_password: newPassword });
  return response.data;
};

export const verifyResetToken = async (token) => {
  const response = await api.get(`/auth/verify-reset-token/${token}`);
  return response.data;
};

// Admin Password Management
export const adminSetTemporaryPassword = async (userId, temporaryPassword) => {
  const response = await api.post(`/admin/users/${userId}/set-password`, { temporary_password: temporaryPassword });
  return response.data;
};

export const adminSendPasswordResetEmail = async (userId) => {
  const response = await api.post(`/admin/users/${userId}/send-reset-email`);
  return response.data;
};

// Google Auth
export const googleAuthSession = async (sessionId) => {
  const response = await api.post('/auth/google/session', null, { params: { session_id: sessionId } });
  if (response.data.token) {
    localStorage.setItem('token', response.data.token);
    localStorage.setItem('user', JSON.stringify(response.data.user));
  }
  return response.data;
};

export const logout = async () => {
  try {
    await api.post('/auth/logout');
  } catch (error) {
    // Ignore errors on logout
  }
  localStorage.removeItem('token');
  localStorage.removeItem('user');
};

// Drivers
export const getDrivers = async (season = 2026) => {
  const response = await api.get('/drivers', { params: { season } });
  return response.data;
};

export const getDriver = async (driverId, season = 2026) => {
  const response = await api.get(`/drivers/${driverId}`, { params: { season } });
  return response.data;
};

// Teams
export const getTeams = async (season = 2026) => {
  const response = await api.get('/teams', { params: { season } });
  return response.data;
};

export const getTeam = async (teamId, season = 2026) => {
  const response = await api.get(`/teams/${teamId}`, { params: { season } });
  return response.data;
};

// Races
export const getRaces = async (season = 2026, status = null) => {
  const response = await api.get('/races', { params: { season, status } });
  return response.data;
};

export const getRace = async (raceId) => {
  const response = await api.get(`/races/${raceId}`);
  return response.data;
};

// Predictions
export const createRacePrediction = async (raceId, raceWinner, top5, fastestLap, safetyCar, virtualSafetyCar, dnfCount, rainRace, sprintData = null, sendConfirmation = true) => {
  const payload = {
    race_id: raceId,
    race_winner: raceWinner,
    top5,
    fastest_lap: fastestLap,
    safety_car: safetyCar,
    virtual_safety_car: virtualSafetyCar,
    dnf_count: dnfCount,
    rain_race: rainRace,
  };
  
  // Add sprint data if provided - always send all fields to prevent partial overwrites
  if (sprintData) {
    payload.sprint_winner = sprintData.sprint_winner || null;
    payload.sprint_pole_winner = sprintData.sprint_pole_winner || null;
    payload.sprint_pole_time = sprintData.sprint_pole_time || null;
  }
  
  const response = await api.post(`/predictions/race?send_confirmation=${sendConfirmation}`, payload);
  return response.data;
};

export const createQualifyingPrediction = async (raceId, qualifying, poleTime) => {
  const response = await api.post('/predictions/qualifying', {
    race_id: raceId,
    qualifying,
    pole_time: poleTime,
  });
  return response.data;
};

export const createChampionshipPrediction = async (worldChampion, constructorChampion, worldChampionTop3 = null, constructorChampionTop3 = null) => {
  const response = await api.post('/predictions/championship', {
    world_champion: worldChampion,
    constructor_champion: constructorChampion,
    world_champion_top3: worldChampionTop3 || [worldChampion, '', ''],
    constructor_champion_top3: constructorChampionTop3 || [constructorChampion, '', ''],
  });
  return response.data;
};

export const getMyPredictions = async (type = null) => {
  const response = await api.get('/predictions/my-predictions', { params: { type } });
  return response.data;
};

export const getMyChampionshipPrediction = async (season = 2026) => {
  const response = await api.get('/predictions/championship', { params: { season } });
  return response.data;
};

export const getUserPredictions = async (userId, season = 2026) => {
  const response = await api.get(`/predictions/user/${userId}`, { params: { season } });
  return response.data;
};

export const deleteMyPrediction = async (raceId) => {
  const response = await api.delete(`/predictions/race/${raceId}`);
  return response.data;
};

// Leaderboard
export const getLeaderboard = async (limit = 0) => {
  const params = {};
  if (limit > 0) params.limit = limit;
  const response = await api.get('/leaderboard', { params });
  return response.data;
};

// Profile
export const getUserProfile = async () => {
  const response = await api.get('/users/profile');
  return response.data;
};

// Prediction Status & Deadlines
export const getPredictionStatus = async (raceId) => {
  const response = await api.get(`/predictions/status/${raceId}`);
  return response.data;
};

export const getChampionshipStatus = async (season = 2026) => {
  const response = await api.get('/predictions/championship-status', { params: { season } });
  return response.data;
};

export const getUpcomingDeadlines = async () => {
  const response = await api.get('/predictions/upcoming-deadlines');
  return response.data;
};

export const checkDeadline = async (raceId) => {
  const response = await api.get(`/predictions/check-deadline/${raceId}`);
  return response.data;
};

export const checkChampionshipDeadline = async (season = 2026) => {
  const response = await api.get('/predictions/check-championship-deadline', { params: { season } });
  return response.data;
};

export const getAllPredictionsForRace = async (raceId) => {
  const response = await api.get(`/predictions/all-predictions/${raceId}`);
  return response.data;
};

export const getAllChampionshipPredictions = async (season = 2026) => {
  const response = await api.get('/predictions/championship-predictions', { params: { season } });
  return response.data;
};

// Scoring
export const getScoringRules = async () => {
  const response = await api.get('/scoring/rules');
  return response.data;
};

// Season Management
export const getAllSeasons = async () => {
  const response = await api.get('/seasons');
  return response.data;
};

export const getCurrentSeason = async () => {
  const response = await api.get('/seasons/current');
  return response.data;
};

export const getSeasonLeaderboard = async (year) => {
  const response = await api.get(`/seasons/${year}/leaderboard`);
  return response.data;
};

export const getSeasonSettings = async (year) => {
  const response = await api.get(`/seasons/${year}/settings`);
  return response.data;
};

export const getAdminDashboardStats = async (season = 2026) => {
  const response = await api.get('/admin/dashboard-stats', { params: { season } });
  return response.data;
};

export const getUserSeasonHistory = async () => {
  const response = await api.get('/users/history');
  return response.data;
};

// Admin functions
export const createSeason = async (year) => {
  const response = await api.post('/admin/seasons/create', null, { params: { year } });
  return response.data;
};

export const copyRacesFromSeason = async (targetYear, sourceYear) => {
  const response = await api.post(`/admin/seasons/${targetYear}/copy-races/${sourceYear}`);
  return response.data;
};

export const startSeason = async (year) => {
  const response = await api.post('/admin/seasons/start', null, { params: { year } });
  return response.data;
};

export const closeSeason = async (year) => {
  const response = await api.post('/admin/seasons/close', null, { 
    params: { year } 
  });
  return response.data;
};

export const updateSeasonSettings = async (year, settings) => {
  const response = await api.put(`/admin/seasons/${year}/settings`, settings);
  return response.data;
};

export const getAllUsers = async () => {
  const response = await api.get('/admin/users');
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/admin/users/${userId}`);
  return response.data;
};

export const deleteUserSeasonData = async (userId, season) => {
  const response = await api.delete(`/admin/users/${userId}/season/${season}`);
  return response.data;
};

export const enterRaceResults = async (raceId, results) => {
  const response = await api.post(`/admin/enter-race-results/${raceId}`, results);
  return response.data;
};

export const sendRaceNewsletter = async (raceId) => {
  const response = await api.post(`/admin/send-race-newsletter/${raceId}`);
  return response.data;
};

export const createTestSeason = async () => {
  const response = await api.post('/admin/create-test-season');
  return response.data;
};

export const deleteTestSeason = async () => {
  const response = await api.delete('/admin/delete-test-season');
  return response.data;
};

export const getTestSeason = async () => {
  const response = await api.get('/test-season');
  return response.data;
};

export const getTestRaces = async () => {
  const response = await api.get('/test-season/races');
  return response.data;
};

export const scoreRace = async (raceId) => {
  const response = await api.post(`/admin/score-race/${raceId}`);
  return response.data;
};

export const rescoreRace = async (raceId) => {
  const response = await api.post(`/admin/races/${raceId}/rescore`);
  return response.data;
};

export const resetSeason = async (year) => {
  const response = await api.post(`/admin/seasons/${year}/reset`);
  return response.data;
};

// Driver management
export const addDriver = async (driverData, season = 2026) => {
  const response = await api.post(`/admin/drivers?season=${season}`, driverData);
  return response.data;
};

export const updateDriver = async (driverId, driverData, season = 2026) => {
  const response = await api.put(`/admin/drivers/${driverId}?season=${season}`, driverData);
  return response.data;
};

export const deleteDriver = async (driverId, season = 2026) => {
  const response = await api.delete(`/admin/drivers/${driverId}?season=${season}`);
  return response.data;
};

// Team management
export const addTeam = async (teamData, season = 2026) => {
  const response = await api.post(`/admin/teams?season=${season}`, teamData);
  return response.data;
};

export const updateTeam = async (teamId, teamData, season = 2026) => {
  const response = await api.put(`/admin/teams/${teamId}?season=${season}`, teamData);
  return response.data;
};

export const deleteTeam = async (teamId, season = 2026) => {
  const response = await api.delete(`/admin/teams/${teamId}?season=${season}`);
  return response.data;
};

// Race management
export const createRace = async (raceData, season = 2026) => {
  const response = await api.post(`/admin/races?season=${season}`, raceData);
  return response.data;
};

export const updateRace = async (raceId, raceData) => {
  const response = await api.put(`/admin/races/${raceId}`, raceData);
  return response.data;
};

export const deleteRace = async (raceId) => {
  const response = await api.delete(`/admin/races/${raceId}`);
  return response.data;
};

// User role management
export const updateUserRole = async (userId, role) => {
  const response = await api.put(`/admin/users/${userId}/role?role=${role}`);
  return response.data;
};

// Admin: Activate/deactivate user
export const toggleUserActivation = async (userId, isActive) => {
  const response = await api.put(`/admin/users/${userId}/activate?is_active=${isActive}`);
  return response.data;
};

// Admin: Toggle user payment status
export const toggleUserPaymentStatus = async (userId, hasPaid) => {
  const response = await api.put(`/admin/users/${userId}/payment-status?has_paid=${hasPaid}`);
  return response.data;
};

// Admin: Update user details
export const adminUpdateUser = async (userId, data) => {
  const response = await api.put(`/admin/users/${userId}/update`, data);
  return response.data;
};

// Admin: Submit predictions for other users
export const adminCreateRacePrediction = async (userId, raceId, raceWinner, top5, fastestLap, safetyCar, virtualSafetyCar, dnfCount, rainRace, sprintWinner, sprintPoleWinner, sprintPoleTime) => {
  const response = await api.post('/admin/predictions/race', {
    user_id: userId,
    race_id: raceId,
    race_winner: raceWinner,
    top5: top5,
    fastest_lap: fastestLap,
    safety_car: safetyCar,
    virtual_safety_car: virtualSafetyCar,
    dnf_count: dnfCount ? String(dnfCount) : null,
    rain_race: rainRace,
    sprint_winner: sprintWinner || null,
    sprint_pole_winner: sprintPoleWinner || null,
    sprint_pole_time: sprintPoleTime || null,
  });
  return response.data;
};

export const adminCreateQualifyingPrediction = async (userId, raceId, qualifying, poleTime) => {
  const response = await api.post('/admin/predictions/qualifying', {
    user_id: userId,
    race_id: raceId,
    qualifying: qualifying,
    pole_time: poleTime,
  });
  return response.data;
};

// Admin: Get all predictions for a race (no deadline check)
export const adminGetAllPredictionsForRace = async (raceId) => {
  const response = await api.get(`/admin/predictions/race/${raceId}`);
  return response.data;
};

export const exportPredictionsToExcel = async () => {
  const response = await api.get('/admin/predictions/export/excel', {
    responseType: 'blob'
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  
  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'F1_Pool_Voorspellingen.xlsx';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename=(.+)/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  
  return true;
};

// Financial settings
export const getFinancialSettings = async (year = 2026) => {
  const response = await api.get(`/seasons/${year}/financial-settings`);
  return response.data;
};

export const updateFinancialSettings = async (year, settings) => {
  const response = await api.put(`/admin/seasons/${year}/financial-settings`, settings);
  return response.data;
};

// User export
export const exportUsers = async () => {
  const response = await api.get('/admin/users/export');
  return response.data;
};

export const exportUsersCSV = async () => {
  const response = await api.get('/admin/users/export/csv', {
    responseType: 'blob'
  });
  return response.data;
};

// Payout Structure
export const getPayoutStructure = async () => {
  const response = await api.get('/payout-structure');
  return response.data;
};

export const updatePayoutStructure = async (structure) => {
  const response = await api.put('/admin/payout-structure', { structure });
  return response.data;
};

// Yearly Bonus
export const getYearlyBonusHolder = async (season = 2026) => {
  const response = await api.get('/yearly-bonus-holder', { params: { season } });
  return response.data;
};

// Payout Compliance Check
export const checkPayoutCompliance = async (participants, costPerRace = 1.0, racesCount = 24) => {
  const response = await api.post('/payout-compliance-check', null, {
    params: { participants, cost_per_race: costPerRace, races_count: racesCount }
  });
  return response.data;
};

// Championship Results
export const getChampionshipResults = async (season = 2026) => {
  const response = await api.get(`/admin/championship-results/${season}`);
  return response.data;
};

export const saveChampionshipResults = async (season, worldChampion, constructorChampion, worldChampionTop5 = null, constructorChampionTop5 = null) => {
  const response = await api.post(`/admin/championship-results/${season}`, {
    world_champion: worldChampion,
    constructor_champion: constructorChampion,
    world_champion_top5: worldChampionTop5,
    constructor_champion_top5: constructorChampionTop5
  });
  return response.data;
};

export const finalizeChampionshipResults = async (season) => {
  const response = await api.post(`/admin/championship-results/${season}/finalize`);
  return response.data;
};

// Admin: Create championship prediction for a user
export const adminCreateChampionshipPrediction = async (userId, season, worldChampionTop3, constructorChampionTop3) => {
  const response = await api.post(`/admin/championship-prediction/${userId}`, {
    season,
    world_champion_top3: worldChampionTop3,
    constructor_champion_top3: constructorChampionTop3
  });
  return response.data;
};

// Admin: Send end of season newsletter
export const sendEndOfSeasonNewsletter = async (season) => {
  const response = await api.post(`/admin/send-end-of-season-newsletter/${season}`);
  return response.data;
};

// Admin: Merge duplicate predictions
export const mergeDuplicatePredictions = async () => {
  const response = await api.post('/admin/merge-duplicate-predictions');
  return response.data;
};

// Admin: Sync race times from OpenF1 API
export const syncRaceTimes = async () => {
  const response = await api.post('/admin/sync-race-times');
  return response.data;
};

export const getPointsAudit = async () => {
  const response = await api.get('/admin/points-audit');
  return response.data;
};

export const rescoreAllRaces = async () => {
  const response = await api.post('/admin/rescore-all-races');
  return response.data;
};

export const syncSprintResults = async () => {
  const response = await api.post('/admin/sync-sprint-results');
  return response.data;
};

export const sendSprintRecalcEmail = async (note, testOnly) => {
  const response = await api.post('/admin/send-sprint-recalc-email', { note, test_only: testOnly });
  return response.data;
};

// Admin: Recalculate leaderboard
export const recalculateLeaderboard = async () => {
  const response = await api.post('/admin/recalculate-leaderboard');
  return response.data;
};



export default api;
